<script setup lang="ts">
import { EditableArea } from "@magnolia/vue-editor";
import { useUser } from "@shopware-pwa/composables-next";
import { Heading } from "~/components/clarity-ui/Heading";
import { Paragraph } from "~/components/clarity-ui/Paragraph";
import { DashboardItem } from "~/components/MyBrita/Dashboard/DashboardItem";
import { useModal } from "~/composables/useModal";
import useAccountTracking from "~/composables/tracking/useAccountTracking";
import EmptyArea from "~/templates/areas/EmptyArea.vue";
import AccountGrid from "~/templates/components/account/AccountGrid.vue";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import AccountNewsletterRegistration from "~/templates/components/shop/account/AccountNewsletterRegistration.vue";
import AccountDeletion from "~/templates/components/shop/account/AccountDeletion.vue";
import ModalComponent from "~/templates/components/ModalComponent.vue";
import type { mgnlHeadline } from "~/types/magnolia";

export interface AccountProfileProps {
  profileNewsletter?: {
    confirmationText?: string;
    informationHeadline?: string;
    interactionId?: string;
    newsletterSubline?: string;
    registrationHeadline?: string;
    resendEmail?: string;
  };
  accountDeletion?: {
    headline?: mgnlHeadline;
  };
  deleteAccountModal?: {
    headline?: string;
    primary?: {
      style?: string;
    };
    richText?: string;
    secondary?: {
      email?: string;
      label?: string;
      link?: string;
      style?: string;
      type?: string;
    };
  };
}

defineProps<AccountProfileProps>();

defineOptions({
  inheritAttrs: false,
});

const { trackAccountModification } = useAccountTracking();
const { user } = useUser();
const deleteAccountController = useModal();
</script>

<template>
  <AccountGrid>
    <div class="account-profile-page flex flex-col">
      <Heading color="blue" tag="h1">
        <CommonLabel path="BWF/shop/account/profile/myProfile" />
      </Heading>

      <div class="mt-6 flex flex-col gap-4 xl:mt-12 xl:gap-6">
        <DashboardItem data-no-tracking>
          <div class="flex h-full flex-col gap-4">
            <Heading size="small" tag="h2">
              <CommonLabel path="BWF/shop/account/profile/customerNo" />
            </Heading>
            <Paragraph>
              {{ user?.customerNumber }}
            </Paragraph>
          </div>
        </DashboardItem>

        <DashboardItem data-no-tracking>
          <AccountPersonalData />
        </DashboardItem>

        <DashboardItem data-no-tracking>
          <AccountAccessData />
        </DashboardItem>

        <DashboardItem class="xl:col-span-full">
          <EditableArea :content="profileNewsletter" :custom-view="AccountNewsletterRegistration" />
        </DashboardItem>

        <DashboardItem class="xl:col-span-full">
          <EditableArea :content="accountDeletion" :custom-view="EmptyArea" />
          <AccountDeletion
            :content="accountDeletion"
            @delete-account="deleteAccountController.open()"
          />
        </DashboardItem>

        <EditableArea :content="deleteAccountModal" :custom-view="EmptyArea" />
        <ModalComponent
          :content="deleteAccountModal"
          :controller="deleteAccountController"
          @secondary-action="trackAccountModification('delete account')"
        >
          <template #headline>Willst Du dein Konto wirklich löschen?*</template>
          <template #richText>
            Damit verlierst Du alle Vorteile wie z.B. einen vereinfachten Kaufprozess, Bestell- und
            Retouren-Status einsehen, Verwaltung der persönlichen Daten, Übersicht deiner
            registrierten Produkte, Abos und Guthaben.
            <br /><br />
            Bitte beachte, dass wir bei Löschung des Kontos nicht automatisch alle deine
            personenbezogene Daten löschen. Solltest Du das wünschen, bitten wir Dich, einen
            entsprechenden Löschwunsch an unseren Customer-Service unter datapolicy@brita.net zu
            senden*
          </template>
          <template #primaryAction>
            <button class="btn btn-ghost" @click="deleteAccountController.close()">
              Abbrechen
            </button>
          </template>
        </ModalComponent>
      </div>
    </div>
  </AccountGrid>
</template>
